import { useCallback, useMemo } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { MealType } from '@fytness/shared';
import { Text } from '@/components/ui/text';
import { Icon } from '~/global/components/icon/Icon';
import { RightSidebar } from '~/layout/components/right-sidebar/RightSidebar';
import { useRightSidebarDrawer } from '~/layout/components/right-sidebar/RightSidebarDrawerContext';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import {
  GetMealDocument,
  MealsByIdsDocument,
  type Meal,
} from '~/graphql/generated/graphql';
import { StringUtils } from '~/global/utils/string.utils';

type MostLoggedRow = { mealId: string; logCount: number };

export interface MealFavouritesPanelProps {
  /** Open day as `yyyy-MM-dd`. */
  date: string;
  /** Open meal type. */
  mealType: MealType;
}

/**
 * Right-rail "favourites" for the meal log: the user's 5 most-logged meals.
 * Tapping one adds it to the open day + meal type, reusing the same create
 * mutation and serving-size resolution as the screen's search flow. The day /
 * meal type are passed in (resolved in-tree by {@link MealLogSidebar}) so the
 * panel works whether it renders in the desktop rail or the mobile drawer (the
 * drawer mounts at the app root, where `useLocalSearchParams` is out of scope).
 */
export const MealFavouritesPanel = ({
  date,
  mealType,
}: MealFavouritesPanelProps) => {
  const { t } = useTranslation('meal');
  const powerSync = usePowerSync();
  // Present only when hosted by the mobile drawer (`undefined` on desktop).
  const drawer = useRightSidebarDrawer();

  const { data: rows = [] } = useLivePowerSyncQuery<MostLoggedRow>(
    db.MealLog.queries.findMostLogged(5),
    [],
  );

  const mealIds = useMemo(
    () => rows.map((r) => r.mealId).filter(Boolean),
    [rows],
  );

  const { data: mealsData } = useQuery(MealsByIdsDocument, {
    variables: { ids: mealIds },
    skip: mealIds.length === 0,
  });

  // Preserve the most-logged ordering from the aggregate query.
  const meals = useMemo<Meal[]>(() => {
    const byId = new Map<string, Meal>();
    (mealsData?.mealsByIds ?? []).forEach((m) => byId.set(m.id, m as Meal));
    return mealIds.map((id) => byId.get(id)).filter(Boolean) as Meal[];
  }, [mealsData, mealIds]);

  const createMutation = useMutation({
    mutationFn: (input: {
      mealId: string;
      consumedAmount: number;
      consumedAt: string;
      mealType: string;
    }) => db.MealLog.mutations.create(powerSync, input),
  });

  const [getMeal] = useLazyQuery(GetMealDocument);

  const handleAdd = useCallback(
    async (meal: Meal) => {
      const result = await getMeal({ variables: { id: meal.id } });
      const fullMeal = result.data?.meal as Meal | undefined;
      if (!fullMeal) return;

      const servingSize = fullMeal.servingSize
        ? (StringUtils.parseFloat(fullMeal.servingSize.toString()) ?? 1)
        : 1;

      createMutation.mutate({
        mealId: meal.id,
        consumedAmount: servingSize,
        consumedAt: date,
        mealType,
      });
      // On mobile, dismiss the drawer so the user sees the updated log.
      drawer?.close();
    },
    [getMeal, date, mealType, createMutation, drawer],
  );

  return (
    <View className="flex-1">
      <RightSidebar.PanelHeader
        icon="Heart"
        title={t('rightSidebar.tabs.favourites')}
      />
      {meals.length === 0 ? (
        <View className="flex-1 items-center justify-center p-6">
          <Text className="font-inter-regular text-sm text-text-secondary text-center">
            {t('rightSidebar.favourites.empty')}
          </Text>
        </View>
      ) : (
        <ScrollView className="flex-1" contentContainerClassName="py-2">
          {meals.map((meal) => (
            <Pressable
              key={meal.id}
              testID="meal-favourite-entry"
              collapsable={false}
              onPress={() => handleAdd(meal)}
              className="flex flex-row items-center gap-3 px-4 py-3 active:opacity-70"
            >
              <View className="items-center justify-center rounded-lg bg-surface-elevated h-11 w-11">
                <Icon
                  name="UtensilsCrossed"
                  size={20}
                  className="text-accent"
                />
              </View>
              <View className="flex-1 gap-0.5">
                <Text
                  testID="meal-favourite-name"
                  className="font-inter-medium text-sm text-text-primary"
                  numberOfLines={1}
                >
                  {meal.name}
                </Text>
                {meal.brand && (
                  <Text
                    className="font-inter-regular text-xs text-text-secondary"
                    numberOfLines={1}
                  >
                    {meal.brand}
                  </Text>
                )}
              </View>
              <Icon name="Plus" size={18} className="text-accent" />
            </Pressable>
          ))}
        </ScrollView>
      )}
    </View>
  );
};
